/**
 * `GET /api/portal/submission/status` (TG-4, m1-declaration-next-steps; ADR-20261022-avfs;
 * `src/api/functions/submission.py`'s `submission_status_http`). Reachable by ANY valid session,
 * owner or contributor alike — unlike submission itself, there is no role check here.
 *
 * This is NOT one of the four self-authored ports in `declaration-submission-port.ts`/`working-
 * copy-port.ts`/etc. (ADR-20260826-ggnj's own pattern) — it exposes one operation with no
 * meaningful request shape and no write side, so a full port interface would add a layer this
 * single function does not need. `App.tsx`'s `RequireSession` (the cold-load bootstrap check,
 * ADR-20261022-avfs's "status read on load") and `DeclarationRouteWrapper`'s `attemptSubmit` (the
 * "any other outcome" status read, AC-6) are this module's only two callers.
 *
 * Fails closed throughout (NFR-restore-fail-closed, mirrored from the API tier's own
 * `api.submission_status` module): a read that cannot complete is `{status:"Failed"}`, never folded
 * into `submitted: false` — a caller must never read a submitted renewal as still open because the
 * read itself broke.
 *
 * ADR-20261018-1skz: gated by `whenSessionTokenHeld`, exactly like every other HTTP-backed read in
 * this directory — no request fires while no session token is held.
 */
import { authenticatedFetch } from "../portal-auth/authenticated-fetch";
import type { ReadOutcome } from "./outcome";
import { whenSessionTokenHeld } from "./http/session-gate";

const STATUS_ROUTE = "/api/portal/submission/status";

export interface SubmissionStatusResult {
  submitted: boolean;
  submittedAt: string | null;
}

function readFailed(httpStatus: number, message: string): ReadOutcome<SubmissionStatusResult> {
  return { status: "Failed", detail: { httpStatus, message } };
}

async function performRead(): Promise<ReadOutcome<SubmissionStatusResult>> {
  let response: Response;
  try {
    response = await authenticatedFetch(STATUS_ROUTE, { method: "GET" });
  } catch (error) {
    return readFailed(0, error instanceof Error ? error.message : String(error));
  }
  if (response.status !== 200) {
    return readFailed(response.status, `GET ${STATUS_ROUTE} returned ${response.status}.`);
  }
  try {
    const body = (await response.json()) as { submitted?: unknown; submittedAt?: unknown };
    if (typeof body.submitted !== "boolean") {
      throw new TypeError(`GET ${STATUS_ROUTE}: 200 response carried no readable "submitted" field.`);
    }
    return {
      status: "Succeeded",
      value: { submitted: body.submitted, submittedAt: typeof body.submittedAt === "string" ? body.submittedAt : null },
    };
  } catch (error) {
    return readFailed(response.status, error instanceof Error ? error.message : String(error));
  }
}

/** Reads the live submission status, gated on a held session token. Never caches -- every call is a
 * fresh read, per ADR-20261022-avfs ("one live source of truth from Renewals API"). */
export function readSubmissionStatus(): Promise<ReadOutcome<SubmissionStatusResult>> {
  return whenSessionTokenHeld(performRead);
}
